import {
  BILLING_PLANS,
  OVERAGE_RATE_PER_MINUTE,
  PAYG_RATE_PER_MINUTE,
  PLAN_CODE_PAYG,
  type BillingPlanCode,
} from './types';

type EnterprisePlanCode = Exclude<BillingPlanCode, 'individual'>;
export type { EnterprisePlanCode };

export interface EnterprisePlanDisplay {
  code: EnterprisePlanCode;
  name: string;
  monthlyPrice: number;
  isMetered: boolean;
  popular: boolean;
  description: string;
  cardFeatures: string[];
}

export const ENTERPRISE_PLAN_DISPLAY: EnterprisePlanDisplay[] = [
  {
    code: 'enterprise_1',
    name: 'Enterprise Starter',
    monthlyPrice: BILLING_PLANS.enterprise_1.monthlyPrice,
    isMetered: false,
    popular: true,
    description: 'For small teams getting started with structured hiring.',
    cardFeatures: [
      `${BILLING_PLANS.enterprise_1.minutesIncluded.toLocaleString()} minutes / month`,
      `Overage at $${OVERAGE_RATE_PER_MINUTE.toFixed(2)}/min`,
      'Team analytics',
      'Custom requirements',
      'ATS tracking system',
    ],
  },
  {
    code: 'enterprise_5',
    name: 'Enterprise Premium',
    monthlyPrice: BILLING_PLANS.enterprise_5.monthlyPrice,
    isMetered: false,
    popular: false,
    description: 'For growing orgs with high-volume screening.',
    cardFeatures: [
      `${BILLING_PLANS.enterprise_5.minutesIncluded.toLocaleString()} minutes / month`,
      `Overage at $${OVERAGE_RATE_PER_MINUTE.toFixed(2)}/min`,
      'Team analytics',
      'Priority support',
      'ATS tracking system',
    ],
  },
  {
    code: PLAN_CODE_PAYG,
    name: 'Pay As You Go',
    monthlyPrice: 0,
    isMetered: true,
    popular: false,
    description: 'No commitments. Pay only for what you use.',
    cardFeatures: [
      'Unlimited interview minutes',
      'Interview recordings',
      'Candidate scorecards',
      'ATS tracking system',
    ],
  },
];

export const ENTERPRISE_PLAN_DISPLAY_BY_CODE = Object.fromEntries(
  ENTERPRISE_PLAN_DISPLAY.map((plan) => [plan.code, plan]),
) as Record<EnterprisePlanCode, EnterprisePlanDisplay>;

export const ENTERPRISE_FEATURE_ROWS: {
  name: string;
  enterprise_1: string | boolean;
  enterprise_5: string | boolean;
  pay_as_you_go: string | boolean;
}[] = [
  {
    name: 'Minutes / month',
    enterprise_1: `${BILLING_PLANS.enterprise_1.minutesIncluded.toLocaleString()} min`,
    enterprise_5: `${BILLING_PLANS.enterprise_5.minutesIncluded.toLocaleString()} min`,
    pay_as_you_go: 'Unlimited',
  },
  {
    name: 'Base monthly fee',
    enterprise_1: `$${BILLING_PLANS.enterprise_1.monthlyPrice}/mo`,
    enterprise_5: `$${BILLING_PLANS.enterprise_5.monthlyPrice}/mo`,
    pay_as_you_go: '$0/mo',
  },
  {
    name: 'Usage rate',
    enterprise_1: `$${OVERAGE_RATE_PER_MINUTE.toFixed(2)}/min overage`,
    enterprise_5: `$${OVERAGE_RATE_PER_MINUTE.toFixed(2)}/min overage`,
    pay_as_you_go: `$${PAYG_RATE_PER_MINUTE.toFixed(2)}/min`,
  },
  {
    name: 'Interview Recordings',
    enterprise_1: true,
    enterprise_5: true,
    pay_as_you_go: true,
  },
  {
    name: 'Custom Requirements',
    enterprise_1: true,
    enterprise_5: true,
    pay_as_you_go: false,
  },
  {
    name: 'Team Analytics',
    enterprise_1: true,
    enterprise_5: true,
    pay_as_you_go: false,
  },
  {
    name: 'Candidate Scorecards',
    enterprise_1: true,
    enterprise_5: true,
    pay_as_you_go: true,
  },
  {
    name: 'Priority Support',
    enterprise_1: false,
    enterprise_5: true,
    pay_as_you_go: false,
  },
  {
    name: 'ATS Tracking',
    enterprise_1: true,
    enterprise_5: true,
    pay_as_you_go: true,
  },
];

export const INDIVIDUAL_PLAN_DISPLAY = {
  code: 'individual' as const,
  name: 'Individual',
  monthlyPrice: BILLING_PLANS.individual.monthlyPrice,
  description: 'Practice interviews on your own schedule with AI feedback.',
  cardFeatures: [
    `${BILLING_PLANS.individual.minutesIncluded.toLocaleString()} minutes / month`,
    `Overage at $${OVERAGE_RATE_PER_MINUTE.toFixed(2)}/min`,
    'AI feedback and analytics',
    'Performance tracking',
  ],
};

export const CANDIDATE_FEATURE_ROWS: { name: string; individual: string | boolean }[] = [
  {
    name: 'Minutes / month',
    individual: `${BILLING_PLANS.individual.minutesIncluded.toLocaleString()} min`,
  },
  { name: 'Usage rate', individual: `$${OVERAGE_RATE_PER_MINUTE.toFixed(2)}/min overage` },
  { name: 'AI Feedback & Analytics', individual: true },
  { name: 'Performance Tracking', individual: true },
];

export const formatEnterprisePrice = (planCode: EnterprisePlanCode) => {
  if (planCode === PLAN_CODE_PAYG) {
    return `$${PAYG_RATE_PER_MINUTE.toFixed(2)}/min`;
  }

  return `$${ENTERPRISE_PLAN_DISPLAY_BY_CODE[planCode].monthlyPrice}/mo`;
};
